/**
 * Utility functions for tenant URL validation and cleanup
 * Centralizes deprecated-domain detection logic to prevent race conditions.
 * Improvements made: stricter hostname parsing, safer logging (no secrets),
 * storage access guards for restricted environments, and conservative cleanup.
 */

/**
 * Check if a URL contains the deprecated domain pattern
 * @param {string} url - URL to check
 * @returns {boolean} - True if URL contains deprecated domain
 */
// Known deprecated hostnames (lowercase). If more hosts are deprecated add them here.
export const DEPRECATED_HOSTNAMES = new Set(['app.iotorq-lean.com']);

/**
 * Extract a hostname from a user-provided URL/host string in a safe way.
 * Accepts bare hostnames (example.com), hostnames with scheme, and full URLs.
 * Returns null for invalid inputs.
 *
 * @param {string} url - URL or hostname to extract from
 * @returns {string|null}
 */
function extractHostname(url) {
    if (!url || typeof url !== 'string') return null;

    const trimmed = url.trim().toLowerCase();

    // If it's clearly a hostname without scheme (no "://"), prefix with https://
    const withScheme = trimmed.includes('://') ? trimmed : `https://${trimmed}`;

    try {
        const parsed = new URL(withScheme);
        return parsed.hostname;
    } catch (e) {
        // Not a valid URL/hostname
        return null;
    }
}

/**
 * Check if a URL/hostname contains or resolves to a deprecated hostname.
 * Matches exact hostname and any subdomain of a deprecated host.
 *
 * @param {string} url - URL or hostname to check
 * @returns {boolean}
 */
export function isDeprecatedDomain(url) {
    const hostname = extractHostname(url);
    if (!hostname) return false;

    for (const deprecated of DEPRECATED_HOSTNAMES) {
        if (hostname === deprecated || hostname.endsWith(`.${deprecated}`)) {
            return true;
        }
    }

    return false;
}

/**
 * Safely get tenant URL from localStorage with validation
 * This prevents storing deprecated domains in the auth store
 * @returns {string|null} - Valid tenant URL or null
 */
export function getSafeTenantURL() {
    try {
        const tenantURL = localStorage.getItem('tenantURL');

        if (isDeprecatedDomain(tenantURL)) {
            // Avoid logging the value (could contain sensitive info).
            console.warn(
                'Deprecated tenant domain detected in localStorage; removing tenant data'
            );
            localStorage.removeItem('tenantURL');
            localStorage.removeItem('tenant');
            return null;
        }

        return typeof tenantURL === 'string' && tenantURL.trim() !== ''
            ? tenantURL.trim()
            : null;
    } catch (err) {
        // In some environments (private mode / restricted storage) accessing localStorage can throw.
        console.warn(
            'Unable to access localStorage while getting tenant URL:',
            err && err.message ? err.message : err
        );
        return null;
    }
}

/**
 * Safely get tenant from localStorage with validation
 * @returns {string|null} - Valid tenant or null
 */
export function getSafeTenant() {
    try {
        const tenant = localStorage.getItem('tenant');
        const tenantURL = localStorage.getItem('tenantURL');

        // If tenantURL is deprecated, tenant is also invalid
        if (isDeprecatedDomain(tenantURL)) {
            console.warn('Deprecated tenantURL detected; removing tenant data');
            localStorage.removeItem('tenant');
            localStorage.removeItem('tenantURL');
            return null;
        }

        return typeof tenant === 'string' && tenant.trim() !== ''
            ? tenant.trim()
            : null;
    } catch (err) {
        console.warn(
            'Unable to access localStorage while getting tenant:',
            err && err.message ? err.message : err
        );
        return null;
    }
}

/**
 * Perform comprehensive cleanup of deprecated domain data
 * This is called by both axios boot and store initialization as a safety net
 */
export function performDeprecatedDomainCleanup() {
    console.warn(
        'Performing deprecated domain cleanup (conservative removal of tenant keys)'
    );

    try {
        // Remove tenant-specific keys to avoid wiping unrelated state
        const keysToRemove = ['tenantURL', 'tenant'];
        keysToRemove.forEach(key => {
            try {
                localStorage.removeItem(key);
            } catch (inner) {
                // ignore individual failures
            }
        });

        console.info('Deprecated domain cleanup completed');
    } catch (err) {
        console.warn(
            'Failed to perform deprecated domain cleanup:',
            err && err.message ? err.message : err
        );
    }
}
